import { useEffect, useState } from "react";
import { Bell, X, Database, CheckCircle, AlertTriangle } from "lucide-react";
import { notificationService } from "../../services/notificationService.js";

function NotificationsPanel({
  isOpen,
  onClose,
  initialNotifications = [],
  onRefetch,
}) {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  // ✅ Sync whenever poller brings fresh data
  useEffect(() => {
    setNotifications(initialNotifications);
  }, [initialNotifications]);

  useEffect(() => {
    let timeoutId;
    if (isOpen) {
      setShouldRender(true);
      // Force an initial hidden frame so open animation always runs.
      setIsVisible(false);
      timeoutId = setTimeout(() => setIsVisible(true), 20);
    } else {
      setIsVisible(false);
      timeoutId = setTimeout(() => setShouldRender(false), 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead({ ids: [id] });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );

      // ✅ same delay
      setTimeout(() => {
        onRefetch?.();
      }, 500);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkAll = async () => {
    try {
      const unread = notifications.filter((n) => !n.read);
      if (unread.length === 0) return;

      const ids = unread.map((n) => n.id);
      await notificationService.markAsRead({ ids });

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

      // ✅ wait 500ms before refetch so backend has time to process
      setTimeout(() => {
        onRefetch?.();
      }, 500);
    } catch (err) {
      console.error(err);
    }
  };

  const getIcon = (outcome) => {
    if (outcome === "success") return CheckCircle;
    if (outcome === "fail") return AlertTriangle;
    return Database;
  };

  const getStyle = (outcome) => {
    if (outcome === "success")
      return "text-[#36a852] bg-green-100 dark:bg-green-900/30";
    if (outcome === "fail") return "text-red-500 bg-red-100 dark:bg-red-900/30";
    return "text-[#1d57a5] bg-blue-100 dark:bg-blue-900/30";
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* BACKDROP */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* PANEL */}
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-[24rem] flex flex-col bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700 transform transition-transform duration-300 ease-out ${isVisible ? "translate-x-0" : "translate-x-full"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50/70 dark:bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <Bell size={18} />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-800 dark:text-white">
                Notifications
              </h3>
              <p className="text-xs text-slate-500">
                {notifications.filter((n) => !n.read).length} unread
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* LIST */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
              <Bell size={36} className="text-slate-200 dark:text-slate-700" />
              <p className="text-sm text-slate-400 font-medium">
                No notifications
              </p>
            </div>
          ) : (
            notifications.map((n) => {
              const Icon = getIcon(n.outcome);
              const style = getStyle(n.outcome);
              return (
                <div
                  key={n.id}
                  onClick={() => handleMarkAsRead(n.id)}
                  className={`p-4 rounded-2xl border cursor-pointer transition-all hover:-translate-y-0.5 ${
                    n.read
                      ? "bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-700/50"
                      : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 shadow-sm"
                  }`}
                >
                  <div className="flex gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${style}`}
                    >
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1 gap-2">
                        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug">
                          {n.title}
                        </h4>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">
                        {n.message}
                      </p>
                      <span className="text-[10px] text-slate-400 font-medium">
                        {
                          new Date(n.createdAtUtc + "Z").toLocaleString()
                        }
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex justify-end">
          <button
            onClick={handleMarkAll}
            className="inline-flex items-center justify-center h-10 px-5 rounded-full bg-[#1d57a5] text-white text-sm font-semibold hover:bg-[#164280] transition-colors shadow-sm"
          >
            Mark all as read
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationsPanel;
